/**
 * FikoGDTVerify - GD&T doğrulama motoru (CATIA "Inspection" karşılığı, canlı)
 *
 * Sembol koymakla kalmaz: gerçek geometriyi ölçüp toleransa göre PASS/FAIL verir.
 * Saf hesaplama — UI yok. Geometriler WORLD-space kabul edilir (cetvel/kumpas
 * zaten world-space geometri çıkarır).
 *
 * Bu sürüm: Form (flatness) + Orientation (diklik/paralellik/açısallık).
 * Sonraki: circularity/cylindricity/position/runout.
 */

import * as THREE from 'three';

export interface GDTSonuc {
  /** Ölçülen sapma değeri (tolerans bölge genişliği, mm) */
  sapma: number;
  /** Tolerans (mm) verilmişse PASS/FAIL */
  gecti?: boolean;
  /** Yönelim ölçümlerinde ölçülen açı (derece) */
  olculenAci?: number;
  /** Nominalden açısal sapma (derece) */
  aciSapma?: number;
  /** İnsan-okunur etiket */
  etiket: string;
}

export interface BestFitPlane {
  nokta: THREE.Vector3;   // düzlem üzerinde bir nokta (centroid)
  normal: THREE.Vector3;  // birim normal
}

// ==================== EN-KÜÇÜK-KARELER DÜZLEM (PCA) ====================

const _v = new THREE.Vector3();

/**
 * Geometrinin best-fit düzlemini bul (least-squares, kovaryans PCA).
 * Normal = en küçük varyans yönü = en büyük iki özvektörün cross product'ı.
 */
export function bestFitPlane(geom: THREE.BufferGeometry): BestFitPlane | null {
  const pos = geom.attributes?.position;
  if (!pos || pos.count < 3) return null;
  const n = pos.count;

  // Centroid
  let cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < n; i++) { cx += pos.getX(i); cy += pos.getY(i); cz += pos.getZ(i); }
  cx /= n; cy /= n; cz /= n;

  // Kovaryans
  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
  for (let i = 0; i < n; i++) {
    const dx = pos.getX(i) - cx, dy = pos.getY(i) - cy, dz = pos.getZ(i) - cz;
    xx += dx * dx; xy += dx * dy; xz += dx * dz;
    yy += dy * dy; yz += dy * dz; zz += dz * dz;
  }

  const cov = [[xx, xy, xz], [xy, yy, yz], [xz, yz, zz]];
  const mul = (m: number[][], v: THREE.Vector3, out: THREE.Vector3) => {
    out.set(
      m[0][0] * v.x + m[0][1] * v.y + m[0][2] * v.z,
      m[1][0] * v.x + m[1][1] * v.y + m[1][2] * v.z,
      m[2][0] * v.x + m[2][1] * v.y + m[2][2] * v.z
    );
  };

  // v1 = en büyük özvektör (power iteration)
  const v1 = new THREE.Vector3(1, 0.3, 0.7).normalize();
  for (let it = 0; it < 30; it++) {
    mul(cov, v1, _v);
    if (_v.lengthSq() < 1e-12) break;
    v1.copy(_v).normalize();
  }

  // Deflasyon: C' = C - λ1 v1 v1ᵀ
  mul(cov, v1, _v);
  const l1 = _v.dot(v1);
  const c2 = [
    [cov[0][0] - l1 * v1.x * v1.x, cov[0][1] - l1 * v1.x * v1.y, cov[0][2] - l1 * v1.x * v1.z],
    [cov[1][0] - l1 * v1.y * v1.x, cov[1][1] - l1 * v1.y * v1.y, cov[1][2] - l1 * v1.y * v1.z],
    [cov[2][0] - l1 * v1.z * v1.x, cov[2][1] - l1 * v1.z * v1.y, cov[2][2] - l1 * v1.z * v1.z],
  ];

  // v2 = ikinci büyük özvektör
  const v2 = new THREE.Vector3(0.7, 1, 0.3).normalize();
  for (let it = 0; it < 30; it++) {
    mul(c2, v2, _v);
    if (_v.lengthSq() < 1e-12) break;
    v2.copy(_v).normalize();
  }

  // normal = v1 × v2 (en küçük varyans yönü)
  const normal = new THREE.Vector3().crossVectors(v1, v2).normalize();
  if (normal.lengthSq() < 1e-9) return null;

  return { nokta: new THREE.Vector3(cx, cy, cz), normal };
}

/** Geometri bbox köşegeni (yönelim bölge genişliği tahmini için) */
function bboxKosegen(geom: THREE.BufferGeometry): number {
  geom.computeBoundingBox();
  const bb = geom.boundingBox;
  if (!bb) return 1;
  return bb.min.distanceTo(bb.max) || 1;
}

/** Yüzey düzlemsel mi? (flatness / bbox köşegeni eşiği). Orientation gating için. */
export function isPlanar(geom: THREE.BufferGeometry, esik = 0.02): boolean {
  const f = flatness(geom);
  if (!f) return false;
  return f.sapma <= bboxKosegen(geom) * esik;
}

// ==================== FORM: FLATNESS (Düzlemsellik) ====================

/**
 * Düzlemsellik: best-fit düzleme tüm noktaların işaretli mesafelerinin
 * (max - min) farkı = noktaları içine alan iki paralel düzlem arası mesafe.
 */
export function flatness(geom: THREE.BufferGeometry, tolerans?: number): GDTSonuc | null {
  const plane = bestFitPlane(geom);
  if (!plane) return null;
  const pos = geom.attributes.position;

  let dMin = Infinity, dMax = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    _v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).sub(plane.nokta);
    const d = _v.dot(plane.normal);
    if (d < dMin) dMin = d;
    if (d > dMax) dMax = d;
  }

  const sapma = dMax - dMin;
  return {
    sapma,
    gecti: tolerans != null ? sapma <= tolerans : undefined,
    etiket: `Düzlemsellik ⏥ ${sapma.toFixed(4)} mm`,
  };
}

// ==================== ORIENTATION (Yönelim) ====================

// ==================== FORM: CIRCULARITY / CYLINDRICITY ====================

/**
 * Silindir ekseni + centroid. Eksen = kovaryansın "tek kalan" özvektörü:
 * silindirde iki radyal özdeğer eşittir (R²/2), eksenel özdeğer farklıdır.
 * En büyük varyans yönünü almak YANLIŞ (kısa/geniş silindirde radyali seçer).
 */
function dominantAxis(geom: THREE.BufferGeometry): { merkez: THREE.Vector3; eksen: THREE.Vector3 } | null {
  const pos = geom.attributes?.position;
  if (!pos || pos.count < 4) return null;
  const n = pos.count;
  let cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < n; i++) { cx += pos.getX(i); cy += pos.getY(i); cz += pos.getZ(i); }
  cx /= n; cy /= n; cz /= n;

  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
  for (let i = 0; i < n; i++) {
    const dx = pos.getX(i) - cx, dy = pos.getY(i) - cy, dz = pos.getZ(i) - cz;
    xx += dx * dx; xy += dx * dy; xz += dx * dz; yy += dy * dy; yz += dy * dz; zz += dz * dz;
  }
  const cov = [[xx, xy, xz], [xy, yy, yz], [xz, yz, zz]];
  const mul = (v: THREE.Vector3, out: THREE.Vector3) => {
    out.set(
      cov[0][0] * v.x + cov[0][1] * v.y + cov[0][2] * v.z,
      cov[1][0] * v.x + cov[1][1] * v.y + cov[1][2] * v.z,
      cov[2][0] * v.x + cov[2][1] * v.y + cov[2][2] * v.z
    );
  };
  const quad = (v: THREE.Vector3) => { mul(v, _v); return _v.dot(v); };
  const powerIter = (seed: THREE.Vector3, m: (v: THREE.Vector3, o: THREE.Vector3) => void) => {
    const v = seed.clone().normalize();
    const t = new THREE.Vector3();
    for (let it = 0; it < 40; it++) { m(v, t); if (t.lengthSq() < 1e-14) break; v.copy(t).normalize(); }
    return v;
  };

  // v1 = en büyük özvektör
  const v1 = powerIter(new THREE.Vector3(1, 0.3, 0.7), mul);
  const l1 = quad(v1);
  // deflasyon → v2 (ikinci)
  const mul2 = (v: THREE.Vector3, out: THREE.Vector3) => {
    mul(v, out);
    out.addScaledVector(v1, -l1 * v.dot(v1));
  };
  const v2 = powerIter(new THREE.Vector3(0.7, 1, 0.3), mul2);
  const l2 = quad(v2);
  // v3 = v1 × v2 (üçüncü)
  const v3 = new THREE.Vector3().crossVectors(v1, v2).normalize();
  const l3 = quad(v3);

  // λ1≥λ2≥λ3 sıralı. Silindirde iki özdeğer eşit (radyal). "Tek kalan" = eksen.
  // |l1-l2| < |l2-l3| ise (l1,l2) eşit çift → eksen v3; değilse eksen v1.
  const eksen = Math.abs(l1 - l2) < Math.abs(l2 - l3) ? v3 : v1;

  return { merkez: new THREE.Vector3(cx, cy, cz), eksen: eksen.normalize() };
}

/**
 * Silindiriklik: en iyi eksene radyal mesafelerin (max - min) farkı
 * = noktaları içine alan iki eş eksenli silindir arası fark.
 */
export function cylindricity(geom: THREE.BufferGeometry, tolerans?: number): GDTSonuc | null {
  const da = dominantAxis(geom);
  if (!da) return null;
  const pos = geom.attributes.position;
  let rMin = Infinity, rMax = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    _v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).sub(da.merkez);
    const ax = _v.dot(da.eksen);
    // radyal bileşen uzunluğu = sqrt(|v|² - ax²)
    const r = Math.sqrt(Math.max(0, _v.lengthSq() - ax * ax));
    if (r < rMin) rMin = r;
    if (r > rMax) rMax = r;
  }
  const sapma = rMax - rMin;
  return {
    sapma,
    gecti: tolerans != null ? sapma <= tolerans : undefined,
    etiket: `Silindiriklik ⌭ ${sapma.toFixed(4)} mm`,
  };
}

/**
 * Dairesellik: tek kesitteki noktaların merkeze radyal mesafe (max - min) farkı.
 * Noktalar best-fit düzleme yansıtılır, merkez = centroid.
 */
export function circularity(geom: THREE.BufferGeometry, tolerans?: number): GDTSonuc | null {
  const plane = bestFitPlane(geom);
  if (!plane) return null;
  const pos = geom.attributes.position;
  // Düzleme yansıt + centroid (merkez)
  const merkez = plane.nokta; // best-fit düzlem centroid'i
  let rMin = Infinity, rMax = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    _v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).sub(merkez);
    const ax = _v.dot(plane.normal);
    const r = Math.sqrt(Math.max(0, _v.lengthSq() - ax * ax)); // düzlem içi radyal
    if (r < rMin) rMin = r;
    if (r > rMax) rMax = r;
  }
  const sapma = rMax - rMin;
  return {
    sapma,
    gecti: tolerans != null ? sapma <= tolerans : undefined,
    etiket: `Dairesellik ○ ${sapma.toFixed(4)} mm`,
  };
}

// ==================== DATUM TABANLI: RUNOUT / KONSANTRİKLİK ====================

/**
 * Toplam runout (salgı): feature yüzeyinin DATUM eksenine göre radyal sapması.
 * Datum = referans silindirik yüzey (ekseni alınır). Parça datum ekseni etrafında
 * döndürülürken komparatörün toplam hareketi = max - min radyal mesafe.
 */
export function runout(
  datumGeom: THREE.BufferGeometry,
  featureGeom: THREE.BufferGeometry,
  tolerans?: number
): GDTSonuc | null {
  const da = dominantAxis(datumGeom);
  if (!da) return null;
  const pos = featureGeom.attributes?.position;
  if (!pos) return null;

  let rMin = Infinity, rMax = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    _v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).sub(da.merkez);
    const ax = _v.dot(da.eksen);
    const r = Math.sqrt(Math.max(0, _v.lengthSq() - ax * ax));
    if (r < rMin) rMin = r;
    if (r > rMax) rMax = r;
  }
  const sapma = rMax - rMin;
  return {
    sapma,
    gecti: tolerans != null ? sapma <= tolerans : undefined,
    etiket: `Toplam Salgı ↗↗ ${sapma.toFixed(4)} mm`,
  };
}

/**
 * Konsantriklik / eş eksenlilik: feature ekseninin datum ekseninden kaçıklığı.
 * Zone = çap cinsinden = 2 × eksen kaçıklığı.
 */
export function konsantriklik(
  datumGeom: THREE.BufferGeometry,
  featureGeom: THREE.BufferGeometry,
  tolerans?: number
): GDTSonuc | null {
  const da = dominantAxis(datumGeom);
  const fa = dominantAxis(featureGeom);
  if (!da || !fa) return null;

  // feature centroid'inin datum ekseninden radyal uzaklığı
  _v.copy(fa.merkez).sub(da.merkez);
  const ax = _v.dot(da.eksen);
  const kacikli = Math.sqrt(Math.max(0, _v.lengthSq() - ax * ax));
  const sapma = 2 * kacikli; // çap bölgesi

  return {
    sapma,
    gecti: tolerans != null ? sapma <= tolerans : undefined,
    etiket: `Eş Eksenlilik ◎ ${sapma.toFixed(4)} mm`,
  };
}

export type YonelimTip = 'diklik' | 'paralellik' | 'acisallik';

/**
 * Yönelim toleransı: toleranslı yüzey ile datum yüzeyi arasındaki açı.
 * - diklik (perpendicularity): nominal 90°
 * - paralellik (parallelism): nominal 0°
 * - acisallik (angularity): nominalAci ile verilir
 *
 * Bölge genişliği (mm) ≈ sin(açıSapma) × toleranslı yüzeyin uzanımı.
 */
export function yonelimSapma(
  geom: THREE.BufferGeometry,
  datumGeom: THREE.BufferGeometry,
  tip: YonelimTip,
  opts: { nominalAci?: number; tolerans?: number } = {}
): GDTSonuc | null {
  const p1 = bestFitPlane(geom);
  const p2 = bestFitPlane(datumGeom);
  if (!p1 || !p2) return null;

  // İki düzlem arası açı = normaller arası akut açı (|dot| ile 0..90°).
  // Normaller paralel → yüzeyler paralel → açı 0. Normaller dik → yüzeyler dik → açı 90.
  const dot = Math.min(1, Math.abs(p1.normal.dot(p2.normal)));
  const yuzeyAcisi = Math.acos(dot) * 180 / Math.PI; // 0..90

  let nominal: number;
  let sembol: string;
  if (tip === 'diklik') { nominal = 90; sembol = 'Diklik ⊥'; }
  else if (tip === 'paralellik') { nominal = 0; sembol = 'Paralellik //'; }
  else { nominal = opts.nominalAci ?? 0; sembol = `Açısallık ∠${nominal}°`; }

  const aciSapma = Math.abs(yuzeyAcisi - nominal);
  const span = bboxKosegen(geom);
  const sapma = Math.sin(aciSapma * Math.PI / 180) * span;

  return {
    sapma,
    olculenAci: yuzeyAcisi,
    aciSapma,
    gecti: opts.tolerans != null ? sapma <= opts.tolerans : undefined,
    etiket: `${sembol} sapma ${aciSapma.toFixed(3)}° (${sapma.toFixed(4)} mm)`,
  };
}
